import { lyveGateway } from './lyveGateway';

// 24-hour persistent (localStorage) cache for gateway responses that feed
// detail drawers. Survives reloads and new sessions. Mirrors the reference
// cachedInvoke contract: returns { data } (or { data, error }).
const PREFIX = 'lyve_api_v1:';
const TTL = 24 * 60 * 60 * 1000;
const inflight = new Map();

function readCache(key) {
  try {
    const raw = localStorage.getItem(PREFIX + key);
    if (!raw) return undefined;
    const { t, d } = JSON.parse(raw);
    if (Date.now() - t > TTL) {
      localStorage.removeItem(PREFIX + key);
      return undefined;
    }
    return d;
  } catch {
    return undefined;
  }
}

function writeCache(key, data) {
  if (data === undefined) return;
  const val = JSON.stringify({ t: Date.now(), d: data });
  try {
    localStorage.setItem(PREFIX + key, val);
  } catch {
    prune();
    try {
      localStorage.setItem(PREFIX + key, val);
    } catch {
      /* ignore */
    }
  }
}

function prune() {
  try {
    const rows = [];
    for (let i = 0; i < localStorage.length; i++) {
      const k = localStorage.key(i);
      if (!k || !k.startsWith(PREFIX)) continue;
      let t = 0;
      try {
        t = JSON.parse(localStorage.getItem(k)).t || 0;
      } catch {
        /* ignore */
      }
      rows.push({ k, t });
    }
    const now = Date.now();
    const live = [];
    rows.forEach((r) => {
      if (now - r.t > TTL) localStorage.removeItem(r.k);
      else live.push(r);
    });
    live.sort((a, b) => a.t - b.t);
    live.slice(0, Math.ceil(live.length / 2)).forEach((r) => localStorage.removeItem(r.k));
  } catch {
    /* ignore */
  }
}

// Drop-in wrapper around the gateway functions. Caches the result for 24h;
// failed/error responses are never cached.
export function cachedInvoke(fnName, payload) {
  const fn = lyveGateway[fnName];
  if (!fn) return Promise.reject(new Error('Unknown gateway fn: ' + fnName));
  const key = fnName + ':' + JSON.stringify(payload || {});
  const hit = readCache(key);
  if (hit !== undefined) return Promise.resolve({ data: hit });
  if (inflight.has(key)) return inflight.get(key);
  const p = fn(payload)
    .then((r) => {
      if (r && !r.error) writeCache(key, r);
      return { data: r };
    })
    .finally(() => {
      inflight.delete(key);
    });
  inflight.set(key, p);
  return p;
}
